import {
	AppPage,
	BusinessInsightComponent as BIC,
	MimaButton,
	PageLoader,
	MimaText,
	MimaFilter,
	MimaDateFilter,
	MimaDropdownFilter,
	MimaTable,
} from '../../components';
import styles from '../../assets/styles/Inventory/Inventory.module.scss';
import { SplitViewMoreContainer } from '../../containers';
import { amountFormatter } from '../../utils/utils';
import { AnimatePresence } from 'framer-motion';
import { transactionStore } from '../../stores';
import React, { useMemo, useState } from 'react';
import { Formik } from 'formik';
import constant from '../../utils/constant';
import { downloadXlsx2 } from '../../utils/download';
import moment from 'moment';

const SplitPayments = ({
	onReset,
	setOnReset,
	filterPeriod,
	resetFilter,
	validationSchema,
	onSubmit,
	closeModal,
	refresh,
	loading,
	openSplitViewMoreModal,
	showViewMoreState,
	filterPeriods,
	getDate,
	isLoading,
	data,
	isFetching,
	limit,
	onLimitChange,
	currentPage,
	setCurrentPage,
	setSearchQuery,
	refetch,
}) => {
	const { splitAnalysis } = transactionStore;

	const tableData = useMemo(() => {
		if (data?.status === constant.Success) {
			return data?.data?.value || [];
		}
		return [];
	}, [data]);

	const tableColumns = [
		{
			Header: 'Description',
			accessor: 'narration',
		},
		{
			Header: 'Customer',
			accessor: 'fullname ',
			Cell: ({ cell }) => {
				const customerName =
					cell.row.original.order?.invoice?.customer?.fullname;
				return <span>{customerName}</span>;
			},
		},
		{
			Header: 'Name',
			accessor: 'beneficiaryAccountName',
		},
		{
			Header: 'Total Amount',
			accessor: 'totalAmount',
			Cell: ({ cell }) => {
				const totalAmount = cell.row.original.totalAmount;
				const currencyCode = cell.row.original.currencyCode;
				const formattedAmount =
					amountFormatter(currencyCode).format(totalAmount);
				return <span>{formattedAmount}</span>;
			},
		},
		{
			Header: 'Split Amount',
			accessor: 'transactionAmount',
			Cell: ({ cell }) => {
				const transactionAmount = cell.row.original.transactionAmount;
				const currencyCode = cell.row.original.currencyCode;
				const formattedAmount =
					amountFormatter(currencyCode).format(transactionAmount);
				return <span>{formattedAmount}</span>;
			},
		},
		{
			Header: 'Deduction',
			accessor: 'deduction',
			Cell: ({ cell }) => {
				const deduction = cell.row.original.payDeductions;
				const formattedAmount = amountFormatter().format(
					deduction.reduce((partialSum, a) => partialSum + a.amount, 0) || 0
				);

				return <span>{formattedAmount}</span>;
			},
		},
		{
			Header: 'Addition',
			accessor: 'addition',
			Cell: ({ cell }) => {
				const payAdditions = cell.row.original.payAdditions;
				const formattedAmount = amountFormatter().format(
					payAdditions.reduce((partialSum, a) => partialSum + a.amount, 0) || 0
				);
				return <span>{formattedAmount}</span>;
			},
		},
		{
			Header: 'Status',
			accessor: 'status',
			Cell: ({ cell }) => {
				const status = cell.value;
				const statusClass = `status ${status === 'CLOSED' ? 'status__green' : status === 'PROCESSING' ? 'status__dark__yellow' : 'status__red'}`;
				return <span className={statusClass}>{status}</span>;
			},
		},
		{
			Header: 'Actions',
			Cell: ({ cell }) => {
				const item = cell.row.original;
				const status = cell.row.original.status;
				return (
					<MimaButton
						title={status === 'PENDING' ? 'Action' : 'More..'}
						variant={status === 'PENDING' ? 'filter' : 'text'}
						onClick={() => showViewMoreState(item)}
						width={8}
					/>
				);
			},
		},
	];

	const printData = tableData?.map(item => ({
		Description: item.narration,
		Customer: item.order?.invoice?.customer?.fullname,
		Name: item.beneficiaryAccountName,
		'Total Amount': amountFormatter(item.currencyCode).format(item.totalAmount),
		'Split Amount': amountFormatter(item.currencyCode).format(
			item.transactionAmount
		),
		Deduction: amountFormatter().format(
			item.deduction?.reduce((partialSum, a) => partialSum + a.amount, 0) || 0
		),
		Addition: amountFormatter().format(
			item.payAdditions?.reduce((partialSum, a) => partialSum + a.amount, 0) ||
				0
		),
		Status: item.status,
	}));

	return (
		<AppPage pageTitle={`Split Payments for ${filterPeriod}`} refresh={refresh}>
			{loading ? (
				<PageLoader title="While we set things up for you" loading={loading} />
			) : (
				<>
					<div className={styles.main__metrics}>
						<BIC
							title="Total Sales"
							amount={amountFormatter().format(splitAnalysis?.totalAmount || 0)}
							styleClass={styles.main__metrics_lightBlue2}
						/>
						<BIC
							title="Split Disbursement Amount"
							amount={amountFormatter().format(
								splitAnalysis?.totalSplitAmount || 0
							)}
							styleClass={styles.main__metrics_yellow}
						/>
						<BIC
							title="Split Payment Profit Margin"
							amount={amountFormatter().format(splitAnalysis.totalProfit || 0)}
							styleClass={styles.main__metrics_green}
						/>

						<BIC
							title="Total Split transactions"
							amount={splitAnalysis.totalCount || 0}
							styleClass={styles.main__metrics_lightBlue}
						/>
					</div>

					{splitAnalysis?.summaries?.length > 0 && (
						<>
							<MimaText mt={5} mb={3} variant="bodyBold" align="center">
								{`Split Payment Analysis for ${filterPeriod}`}
							</MimaText>
							<div className={styles.threeCards}>
								{splitAnalysis?.summaries?.map((analysis, i) => (
									<div key={i} className={styles.singleCard}>
										<MimaText variant="subtitleBold">
											{' '}
											{analysis._id.status}{' '}
										</MimaText>
										<MimaText labelTitle="Total Amount">
											{amountFormatter().format(analysis.totalAmount)}
										</MimaText>
										<MimaText labelTitle="Split Amount">
											{amountFormatter().format(analysis.splitTotal)}
										</MimaText>
										<MimaText labelTitle="Count">
											{analysis.totalCount}
										</MimaText>
									</div>
								))}
							</div>
						</>
					)}

					<MimaText mt={3} variant="bodyBold" align="center">
						Split Payment Records
					</MimaText>

					<div className={styles.btnClass}>
						<Formik
							initialValues={{
								startDate: '',
								endDate: '',
								period: '',
								periodText: '',
							}}
							validationSchema={validationSchema}
							onSubmit={onSubmit}>
							{({
								handleChange,
								handleBlur,
								handleSubmit,
								setFieldValue,
								values,
								errors,
								touched,
							}) => (
								<div style={{ display: 'flex', width: '38rem' }}>
									<MimaFilter
										filterClick={() => {
											setOnReset(false);
											handleSubmit();
										}}
										loading={loading}
										resetClick={() => {
											setOnReset(true);
											resetFilter();
										}}>
										<div style={{ display: 'flex', flexDirection: 'column' }}>
											<MimaDropdownFilter
												labelTitle="Filter period"
												placeholder="Select Period"
												variant="medium"
												name="period"
												data={filterPeriods}
												onChange={data => {
													const dates = getDate(data.key);
													setFieldValue('period', data.key);
													setFieldValue('startDate', dates.startDate);
													setFieldValue('endDate', dates.endDate);
													setFieldValue('periodText', data.value);
												}}
												error={errors.period}
												touched={touched.period}
											/>
											{values.period === 'custom' && (
												<div
													style={{
														display: 'flex',
														gap: '2rem',
														marginTop: '2rem',
													}}>
													<MimaDateFilter
														labelTitle="start date"
														name="startDate"
														value={!onReset ? values.startDate : ''}
														onChange={text => {
															setOnReset(false);
															setFieldValue('startDate', text);
														}}
														onBlur={handleBlur}
														width={18}
														touched={touched.startDate}
														error={errors.startDate}
													/>
													<MimaDateFilter
														labelTitle="End date"
														name="endDate"
														value={!onReset ? values.endDate : ''}
														onChange={text => {
															setOnReset(false);
															setFieldValue('endDate', text);
														}}
														onBlur={handleBlur}
														width={18}
														touched={touched.endDate}
														error={errors.endDate}
													/>
												</div>
											)}
										</div>
									</MimaFilter>
								</div>
							)}
						</Formik>
						<MimaButton
							variant="contained"
							title="Download Split Payment Report"
							onClick={() => {
								downloadXlsx2(printData, `Split Payment Report`);
							}}
							height={5}
						/>
					</div>

					<MimaTable
						tableData={tableData}
						tableColumns={tableColumns}
						searchPlaceholder="Search Subscription"
						searchVariant="wide"
						totalItems={data?.data?.totalCounts}
						onLimitChange={onLimitChange}
						isLoading={isLoading}
						isFetching={isFetching}
						limit={limit}
						currentPage={currentPage}
						setCurrentPage={setCurrentPage}
						setSearchQuery={setSearchQuery}
					/>

					{openSplitViewMoreModal && (
						<div className="modal">
							<AnimatePresence>
								{openSplitViewMoreModal && (
									<SplitViewMoreContainer closeModal={closeModal} />
								)}
							</AnimatePresence>
						</div>
					)}
				</>
			)}
		</AppPage>
	);
};

export default SplitPayments;
